import { AbstractDestroyable, Callback1, destroySafe, ListStore, memone, Store, StoreObserver, VoidCallback } from '@deqo/foundation';
import { PodSessionWithRecordSetFeature, RecordData } from '@deqo/podopolis';
import { EditorPodSession } from '../../app/canvas/EditorPodSession';

export interface PodRecord<T extends RecordData> extends Store<T> {

    readonly id: string;

    readonly future: boolean;

    get<K extends keyof T>(key: K): T[K];

    mutate(patch: Partial<T>): void;
}

export interface PodRecordSet<T extends RecordData = RecordData> extends ListStore<PodRecord<T>> {

    readonly name: string;

    get(id: string): PodRecord<T> | null;

    getForFuture(id: string): PodRecord<T>;

    waitForFuture(id: string, timeout?: number): Promise<PodRecord<T>>;
}

class PodRecordImpl<T extends RecordData> extends AbstractDestroyable implements PodRecord<T> {

    private observers: Set<StoreObserver<T>>;
    private state: T;
    private empty = true;

    constructor(public readonly id: string, private patchCallback: (patch: RecordData) => void) {
        super();
        this.observers = new Set();
        this.state = { id } as any;
    }

    public get future() {
        return this.empty;
    }

    public get<K extends keyof T>(key: K): T[K] {
        return this.state[key];
    }

    public getState(): Readonly<T> {
        return this.state;
    }

    public observe(observer: StoreObserver<T>): VoidCallback;
    public observe<P>(path: Callback1<P, T>, observer: StoreObserver<P>): VoidCallback;
    public observe(path: any, observer?: any) {
        if (!!observer) throw new Error('PodRecordImpl: path paramter not supported');
        observer = path;

        this.observers.add(observer);
        return () => this.observers.delete(observer);
    }

    public mutate(patch: Partial<T>): void {
        this.patchCallback({ ...patch, id: this.id });
    }

    public setState(next: T) {
        const prev = this.state;
        this.state = next;
        this.empty = false;
        for (const o of this.observers) {
            o(next, prev);
        }
    }
}

class PodRecordSetImpl<T extends RecordData> extends ListStore<PodRecordImpl<T>> implements PodRecordSet {

    constructor(public readonly name: string, private pod: PodSessionWithRecordSetFeature) {
        super();
        this.burden(
            () => this.state.forEach(r => destroySafe(r)),
            this.pod.on('recordUpdate', ([n, s]) => {
                if (n === this.name) {
                    this.onRecordUpdate(s);
                }
            })
        );
    }

    private get index() {
        return this.getStateAsIndex(this.state);
    }

    public get(id: string): PodRecord<T> | null {
        return this.index.get(id) ?? null;
    }

    public getForFuture(id: string): PodRecord<T> {
        this.allocateIfRequired(id);
        return this.index.get(id)!;
    }

    public waitForFuture(id: string, timeout = 120 * 1000): Promise<PodRecord<T>> {
        const future = this.getForFuture(id);
        return new Promise<PodRecord<T>>((resolve, reject) => {
            const timer = setTimeout(() => {
                unobserve();
                reject(new Error(`Timeout waiting for record ${id} in set ${this.name}`));
            }, timeout);
            const unobserve = future.observe(() => {
                clearTimeout(timer);
                unobserve();
                resolve(future);
            });
        });
    }

    @memone()
    private getStateAsIndex(state: PodRecordImpl<T>[]) {
        return new Map(state.map(r => [r.id, r]));
    }

    private allocateIfRequired(id: string) {
        if (!this.index.has(id)) {
            const record = new PodRecordImpl<T>(id, this.submitRecordPatch.bind(this));
            this.updateState(s => void (s.push(record)));
        }
    }

    private submitRecordPatch(patch: RecordData) {
        const latest = this.pod.patchRecord(this.name, patch);
        const record = this.index.get(latest.id)!;
        record.setState(latest as any);
    }

    private onRecordUpdate(latest: RecordData) {
        this.allocateIfRequired(latest.id);
        const record = this.index.get(latest.id)!;
        record.setState(latest as any);
    }
}

export class RecordSetManager extends AbstractDestroyable {

    private readonly subscribed = new Map<string, PodRecordSet>();

    constructor(private pod: EditorPodSession) {
        super();
        this.burden(() => this.subscribed.forEach(s => destroySafe(s)));
    }

    public resolve<T extends RecordData = RecordData>(recordSetName: string): PodRecordSet<T> {
        if (!this.subscribed.has(recordSetName)) {
            this.subscribed.set(recordSetName, new PodRecordSetImpl<T>(recordSetName, this.pod));
            this.pod.subscribeRecordSet(recordSetName);
        }
        return this.subscribed.get(recordSetName) as any;
    }

    public toArray() {
        return Array.from(this.subscribed.values());
    }
}
